import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
    Typography,
    TextField
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Grid from '@mui/material/Grid';
import Box from "@mui/material/Box";
import './lodging.css';

// Half dozen tailored progression statuses for dynamic lodging tracking
const LODGING_STATUSES = [
    { value: 0, label: "💡 Researching / Shortlist", color: "#64748b", bg: "rgba(100, 116, 139, 0.1)" },
    { value: 1, label: "⏳ Hold / Payment Pending", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)" },
    { value: 2, label: "✅ Confirmed & Booked", color: "#10b981", bg: "rgba(16, 185, 129, 0.1)" },
    { value: 3, label: "🔑 Checked In / Active", color: "#3b82f6", bg: "rgba(59, 130, 246, 0.1)" },
    { value: 4, label: "🏁 Checked Out & Settled", color: "#8b5cf6", bg: "rgba(139, 92, 246, 0.1)" },
    { value: 5, label: "❌ Cancelled by Us", color: "#ef4444", bg: "rgba(239, 68, 68, 0.1)" },
    { value: 6, label: "🚫 Cancelled by Agent", color: "#b91c1c", bg: "rgba(185, 28, 28, 0.1)" }
];

function LodgingDialogEditDetails({ isOpen, onClose, eventLodging, setEventLodging }) {
    const [lodgingName, setLodgingName] = useState("");
    const [bookedThrough, setBookedThrough] = useState("");
    const [confirmationNumber, setConfirmationNumber] = useState("");
    const [reservationName, setReservationName] = useState("");
    const [reservationNumber, setReservationNumber] = useState("");
    const [gateCode, setGateCode] = useState("");
    const [doorCode, setDoorCode] = useState("");
    const [wifiName, setWifiName] = useState("");
    const [wifiPassword, setWifiPassword] = useState("");
    const [notes, setNotes] = useState("");

    const [lodgingStatus, setLodgingStatus] = useState(0);
    const [anchorStatusEl, setAnchorStatusEl] = useState(null);

    const initialValues = useRef({});

    useEffect(() => {
        if (isOpen && eventLodging) {
            setLodgingName(eventLodging.name || "");
            setBookedThrough(eventLodging.booked_through || "");
            setConfirmationNumber(eventLodging.confirmation_number || "");
            setReservationName(eventLodging.reservation_name || "");
            setReservationNumber(eventLodging.reservation_number || "");
            setGateCode(eventLodging.gate_code || "");
            setDoorCode(eventLodging.door_code || "");
            setWifiName(eventLodging.wifi_name || "");
            setWifiPassword(eventLodging.wifi_password || "");
            setNotes(eventLodging.notes || "");
            setLodgingStatus(typeof eventLodging.status === "number" ? eventLodging.status : 0);

            initialValues.current = {
                lodgingName: eventLodging.name || "",
                bookedThrough: eventLodging.booked_through || "",
                confirmationNumber: eventLodging.confirmation_number || "",
                reservationName: eventLodging.reservation_name || "",
                reservationNumber: eventLodging.reservation_number || "",
                gateCode: eventLodging.gate_code || "",
                doorCode: eventLodging.door_code || "",
                wifiName: eventLodging.wifi_name || "",
                wifiPassword: eventLodging.wifi_password || "",
                notes: eventLodging.notes || "",
                lodgingStatus: typeof eventLodging.status === "number" ? eventLodging.status : 0
            };
        }
    }, [isOpen, eventLodging]);

    const handleOpenStatusMenu = (event) => setAnchorStatusEl(event.currentTarget);
    const handleCloseStatusMenu = () => setAnchorStatusEl(null);
    const handleSelectStatus = (statusValue) => {
        setLodgingStatus(statusValue);
        handleCloseStatusMenu();
    };

    function handleLodgingNameChange(event) { setLodgingName(event.target.value); }
    function handleBookedThroughChange(event) { setBookedThrough(event.target.value); }
    function handleConfirmationNumberChange(event) { setConfirmationNumber(event.target.value); }
    function handleReservationNameChange(event) { setReservationName(event.target.value); }
    function handleReservationNumberChange(event) { setReservationNumber(event.target.value); }
    function handleNotesChange(event) { setNotes(event.target.value); }
    function handleGateCodeChange(event) { setGateCode(event.target.value); }
    function handleDoorCodeChange(event) { setDoorCode(event.target.value); }
    function handleWifiNameChange(event) { setWifiName(event.target.value); }
    function handleWifiPasswordChange(event) { setWifiPassword(event.target.value); }

    function saveEventLodgingSettings() {

        const updatedLodging = {
            ...eventLodging,
            name: lodgingName,
            location: {
                ...eventLodging.location,
                name: lodgingName,
            },
            booked_through: bookedThrough,
            confirmation_number: confirmationNumber,
            reservation_name: reservationName,
            reservation_number: reservationNumber,
            gate_code: gateCode,
            door_code: doorCode,
            wifi_name: wifiName,
            wifi_password: wifiPassword,
            notes: notes,
            status: lodgingStatus
        };

        setEventLodging(updatedLodging);

        initialValues.current = {
            lodgingName, bookedThrough, confirmationNumber, reservationName,
            reservationNumber, gateCode, doorCode, wifiName, wifiPassword, notes, lodgingStatus
        };

        onClose();
    }

    const currentStatusObject = LODGING_STATUSES.find(s => s.value === lodgingStatus) || LODGING_STATUSES[0];

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    minWidth: 350,
                    maxWidth: 520,
                    width: '100%',
                    borderRadius: '24px',
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(25px)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                },
            }}
        >
            <DialogTitle sx={{ color: '#1e293b', textAlign: 'center', fontWeight: 800, fontSize: '1.2rem', pt: 3, pb: 1 }}>
                Lodging Details Configuration
            </DialogTitle>

            <IconButton onClick={onClose} sx={{ position: 'absolute', right: 16, top: 16, color: '#64748b' }}>
                <i className="icon-cross" style={{ fontSize: '14px' }} />
            </IconButton>

            <DialogContent sx={{ '&::-webkit-scrollbar': { display: 'none' } }}>
                {/* Status Dropdown Menu */}
                <Box
                    sx={{
                        p: 1.5,
                        mb: 2.5,
                        borderRadius: '16px',
                        background: 'rgba(255, 255, 255, 0.45)',
                        border: '1px solid rgba(255, 255, 255, 0.6)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#475569', pl: 0.5 }}>
                        Reservation Status:
                    </Typography>

                    <Button
                        variant="text"
                        onClick={handleOpenStatusMenu}
                        sx={{
                            backgroundColor: currentStatusObject.bg,
                            color: currentStatusObject.color,
                            fontWeight: 800,
                            fontSize: '12px',
                            textTransform: 'none',
                            borderRadius: '10px',
                            px: 2,
                            py: 0.75,
                            border: `1px solid ${currentStatusObject.color}20`,
                            '&:hover': { backgroundColor: currentStatusObject.bg, opacity: 0.85 }
                        }}
                    >
                        {currentStatusObject.label} ▾
                    </Button>

                    <Menu
                        anchorEl={anchorStatusEl}
                        open={Boolean(anchorStatusEl)}
                        onClose={handleCloseStatusMenu}
                        PaperProps={{ sx: { borderRadius: '14px', p: 0.5, boxShadow: '0 10px 25px rgba(0,0,0,0.08)' } }}
                    >
                        {LODGING_STATUSES.map((status) => (
                            <MenuItem
                                key={status.value}
                                onClick={() => handleSelectStatus(status.value)}
                                selected={status.value === lodgingStatus}
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    borderRadius: '8px',
                                    my: 0.25,
                                    color: status.color,
                                    '&.Mui-selected': { backgroundColor: status.bg },
                                    '&:hover': { backgroundColor: status.bg }
                                }}
                            >
                                {status.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>

                <Grid container spacing={2}>
                    {/* Basic Info */}
                    <Grid item xs={12}>
                        <TextField fullWidth size="small" label="Lodging Name" value={lodgingName} onChange={handleLodgingNameChange} InputProps={{ sx: { borderRadius: '12px', background: '#fff' } }} />
                    </Grid>

                    {/* Booking Details Section Header */}
                    <Grid item xs={12} sx={{ mt: 0.5 }}>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', color: '#1976d2', fontWeight: 800, letterSpacing: '0.5px' }}>Booking & Registry</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Booked Through" value={bookedThrough} onChange={handleBookedThroughChange} InputProps={{ sx: { borderRadius: '12px', background: '#fff' } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Confirmation Number" value={confirmationNumber} onChange={handleConfirmationNumberChange} InputProps={{ sx: { borderRadius: '12px', background: '#fff' } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Reservation Name" value={reservationName} onChange={handleReservationNameChange} InputProps={{ sx: { borderRadius: '12px', background: '#fff' } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Reservation Phone/Number" value={reservationNumber} onChange={handleReservationNumberChange} InputProps={{ sx: { borderRadius: '12px', background: '#fff' } }} />
                    </Grid>

                    {/* Access & Logistics Header */}
                    <Grid item xs={12} sx={{ mt: 0.5 }}>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', color: '#1976d2', fontWeight: 800, letterSpacing: '0.5px' }}>Access Keys & Onsite Wi-Fi</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Gate Code" value={gateCode} onChange={handleGateCodeChange} InputProps={{ sx: { borderRadius: '12px', background: '#fff' } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Door Code" value={doorCode} onChange={handleDoorCodeChange} InputProps={{ sx: { borderRadius: '12px', background: '#fff' } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Wifi Name" value={wifiName} onChange={handleWifiNameChange} InputProps={{ sx: { borderRadius: '12px', background: '#fff' } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Wifi Password" value={wifiPassword} onChange={handleWifiPasswordChange} InputProps={{ sx: { borderRadius: '12px', background: '#fff' } }} />
                    </Grid>

                    {/* Notes */}
                    <Grid item xs={12} sx={{ mt: 0.5 }}>
                        <TextField fullWidth id="edit_lodging_notes" label="Stay Notes" multiline rows={3} value={notes} onChange={handleNotesChange} InputProps={{ sx: { borderRadius: '12px', background: '#fff' } }} />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: '0 24px 24px 24px', gap: 1 }}>
                <Button onClick={onClose} sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
                <Button
                    onClick={saveEventLodgingSettings}
                    variant="contained"
                    sx={{
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        boxShadow: '0 4px 14px rgba(25, 118, 210, 0.3)',
                        borderRadius: '10px',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 3,
                        '&:hover': { background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)' }
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default LodgingDialogEditDetails;